import { useRef } from 'react';
import { Card, CardActionArea, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import { getIconComponent } from '../../utils/icons';
import { type AppConfig } from '../../types';
import { useTruncated } from '../../hooks/useTruncated';
import { PinButton } from './PinButton';

interface ProductTileProps {
  readonly app: AppConfig;
  readonly isPinned: boolean;
  readonly pinDisabled?: boolean;
  readonly onTogglePin: (basePath: string) => void;
  readonly onNavigate: (basePath: string) => void;
}

export function ProductTile({
  app,
  isPinned,
  pinDisabled,
  onTogglePin,
  onNavigate,
}: ProductTileProps) {
  const IconComponent = getIconComponent(app.icon);
  const descriptionRef = useRef<HTMLElement>(null);
  const isDescriptionTruncated = useTruncated(descriptionRef, app.description);

  return (
    <Card
      variant="outlined"
      sx={{
        position: 'relative',
        height: '100%',
        transition: (theme) =>
          theme.transitions.create(['border-color', 'box-shadow', 'transform'], {
            duration: theme.transitions.duration.shortest,
          }),
        ...(isPinned && { borderColor: 'primary.main' }),
        '&:hover': {
          borderColor: 'primary.main',
          transform: 'translateY(-1px)',
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      <CardActionArea
        onClick={() => onNavigate(app.basePath)}
        sx={{ height: '100%', p: 2.5, pt: 0, alignItems: 'flex-start' }}
      >
        <CardContent sx={{ p: 0, width: '100%' }}>
          {/* pr leaves room for the absolutely-positioned pin button */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 2 }}>
            <IconComponent
              aria-label={app.displayName}
              sx={{ fontSize: 28, color: 'text.primary' }}
            />
            <Typography variant="bodySmall" fontWeight={600}>
              {app.displayName}
            </Typography>
          </Stack>
          {app.description && (
            <Tooltip
              title={isDescriptionTruncated ? app.description : ''}
              arrow
              enterNextDelay={300}
            >
              <Typography
                ref={descriptionRef}
                variant="bodyXSmall"
                sx={{
                  mt: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {app.description}
              </Typography>
            </Tooltip>
          )}
        </CardContent>
      </CardActionArea>
      {/* Sibling of CardActionArea (not a child) — pin click cannot bubble into navigation */}
      <span style={{ position: 'absolute', top: 12, right: 12 }}>
        <PinButton
          displayName={app.displayName}
          isPinned={isPinned}
          pinDisabled={pinDisabled}
          onTogglePin={() => onTogglePin(app.basePath)}
        />
      </span>
    </Card>
  );
}
